import React, { useEffect, useState } from "react";

import { ThemeProvider, useTheme } from "styled-components";

import Content from "../content/Content";
import Settings from "../drawers/settings/Settings";
import Templates from "../drawers/templates/Templates";
import Header from "../header/Header";
import Sidebar from "../sidebar/Sidebar";
import AdvancedState from "../../../components/advanced-state/AdvancedState";
import EditDisabled from "../../../components/edit-disabled/EditDisabled";
import WarningModal from "./WarningModal";
import { useFetchLibraryMacros } from "../../../hooks/use-fetch-library-macros";
import { useMacroData } from "../../../hooks/use-macro-data";
import { useStyleguideUserPermission } from "../../../hooks/use-styleguide-userpermission";
import { macroStores } from "../../../stores/export-stores";
import { useOpticsStore } from "../../../stores/use-optics-store";
import { handleStoredData } from "./handlers/handle-stored-data";
import { handleSaveMacro, saveAndClose } from "./handlers/save-macro";
import {
  EditorContainer,
  EditorContentContainer,
  EditorMainContainer,
} from "./styles";
import { useStyleguideSetup } from "../../../hooks/use-styleguide-setup";
import { MACRO_KEY, WARNING_DIALOGUE } from "../../defaults";
import {
  MACROS_WITHOUT_SETTINGS,
  MACROS_WITHOUT_SIDEBAR,
} from "../sidebar/defaults";
import { checkForWarnings } from "./handlers/warning-modal";

const Editor = ({ editorMacro }) => {
  const [contentLoaded, setContentLoaded] = useState(false);

  const theme = useTheme();

  const {
    drawers,
    setDrawers,
    macro,
    setMacro,
    setHasStyleguidePermission,
    libraryMacrosSetters,
    setStyleguideSetup,
    advancedMacroEmptyState,
    isEditingOldMacro,
    setIsEditingOldMacro,
    isWarningModalOpen,
    setIsWarningModalOpen,
  } = useOpticsStore((state) => ({
    drawers: state.drawers,
    setDrawers: state.setDrawers,
    macro: state.macro,
    setMacro: state.setMacro,
    setHasStyleguidePermission: state.setHasStyleguidePermission,
    libraryMacrosSetters: {
      setLibraryButtons: state.setLibraryButtons,
      setLibraryPanels: state.setLibraryPanels,
      setLibraryCards: state.setLibraryCards,
    },
    setStyleguideSetup: state.setStyleguideSetup,
    advancedMacroEmptyState: state.advancedMacroEmptyState,
    isEditingOldMacro: state.isEditingOldMacro,
    setIsEditingOldMacro: state.setIsEditingOldMacro,
    isWarningModalOpen: state.isWarningModalOpen,
    setIsWarningModalOpen: state.setIsWarningModalOpen,
  }));

  const { macroDataSetter, getData, resetStore } = macroStores[macro]?.(
    (state) => ({
      macroDataSetter: state?.setData,
      getData: state?.getData,
      resetStore: state?.resetStore,
    })
  );
  const opticsData = useMacroData();
  const styleguideSetup = useStyleguideSetup();

  useStyleguideUserPermission({ setter: setHasStyleguidePermission });
  useFetchLibraryMacros({ setters: libraryMacrosSetters });

  const macroData = macroStores[macro]?.((state) => state?.data || {});
  const hasMacroData = Array.isArray(macroData)
    ? !!macroData.length
    : !!Object.keys(macroData).length;

  useEffect(() => {
    if (Object.keys(styleguideSetup).length) {
      setStyleguideSetup(styleguideSetup);
    }
  }, [styleguideSetup, setStyleguideSetup]);

  useEffect(() => {
    const macroKey = opticsData?.macro || editorMacro || MACRO_KEY.OVERVIEW_KEY;
    setMacro({ key: macroKey });
  }, [editorMacro, opticsData, setMacro]);

  useEffect(() => {
    if (contentLoaded || !macro) return;

    if (macro && opticsData) {
      const { content, isOldMacro } = handleStoredData(macro, opticsData);
      if (typeof macroDataSetter === "function" && content) {
        macroDataSetter(content);
        setContentLoaded(true);
      }
      setIsEditingOldMacro(isOldMacro);
    }
  }, [macro, opticsData, setIsEditingOldMacro, macroDataSetter, contentLoaded]);

  const hasWarningDialogue = {
    EMPTY_LINK: WARNING_DIALOGUE.EMPTY_LINK.includes(macro || editorMacro),
  };

  const handleOnClick = () => {
    if (hasMacroData) {
      const dataToSave = getData();
      const data = handleSaveMacro(macro, dataToSave);
      if (hasWarningDialogue.EMPTY_LINK && !isWarningModalOpen) {
        const hasWarnings = checkForWarnings({ macro, data: dataToSave });

        if (hasWarnings) {
          setIsWarningModalOpen(true);
        } else {
          saveAndClose({ data });
        }
      } else {
        saveAndClose({ data });
      }
    }
  };

  const showTemplates =
    !!macro && !advancedMacroEmptyState && macro !== MACRO_KEY.OVERVIEW_KEY;
  const showSidebar = !MACROS_WITHOUT_SIDEBAR.some(
    (independentMacro) => independentMacro.key === editorMacro
  );
  const showSettings =
    !MACROS_WITHOUT_SETTINGS.some((noSidebar) => noSidebar.key === macro) &&
    drawers.settings &&
    !advancedMacroEmptyState;

  if (isEditingOldMacro)
    return (
      <EditDisabled
        macro={macro}
        handleClickOnRebuild={() => {
          resetStore();
          setDrawers({ templates: true, settings: false });
          setIsEditingOldMacro(false);
        }}
      />
    );

  return (
    <ThemeProvider theme={theme}>
      <EditorContainer>
        <Header onClick={handleOnClick} hasSave={hasMacroData} />
        <EditorMainContainer>
          {showSidebar && <Sidebar />}
          {showTemplates && <Templates />}
          <EditorContentContainer>
            {advancedMacroEmptyState ? <AdvancedState /> : <Content />}
          </EditorContentContainer>

          {showSettings && <Settings />}
        </EditorMainContainer>
      </EditorContainer>

      <WarningModal
        isOpen={isWarningModalOpen}
        onClose={() => setIsWarningModalOpen(false)}
        onConfirm={handleOnClick}
        macroName={macro || editorMacro}
      />
    </ThemeProvider>
  );
};

export default Editor;
